import {EmbeddingModelV1, LanguageModelV1, ProviderV1,} from '@ai-sdk/provider';
import {FetchFunction, loadApiKey, withoutTrailingSlash,} from '@ai-sdk/provider-utils';
import {DifyChatLanguageModel} from './dify-chat-language-model';
import {DifyChatModelId, DifyChatSettings,} from './dify-chat-settings';
import {DifyEmbeddingModel} from './dify-embedding-model';
import {DifyEmbeddingModelId, DifyEmbeddingSettings,} from './dify-embedding-settings';

export interface DifyProvider extends ProviderV1 {
    (
        modelId: DifyChatModelId,
        settings?: DifyChatSettings,
    ): LanguageModelV1;

    /**
     Creates a model for text generation.
     */
    languageModel(
        modelId: DifyChatModelId,
        settings?: DifyChatSettings,
    ): LanguageModelV1;

    /**
     Creates a model for text generation.
     */
    chat(
        modelId: DifyChatModelId,
        settings?: DifyChatSettings,
    ): LanguageModelV1;

    /**
     @deprecated Use `textEmbeddingModel()` instead.
     */
    embedding(
        modelId: DifyEmbeddingModelId,
        settings?: DifyEmbeddingSettings,
    ): EmbeddingModelV1<string>;

    /**
     @deprecated Use `textEmbeddingModel()` instead.
     */
    textEmbedding(
        modelId: DifyEmbeddingModelId,
        settings?: DifyEmbeddingSettings,
    ): EmbeddingModelV1<string>;

    textEmbeddingModel: (
        modelId: DifyEmbeddingModelId,
        settings?: DifyEmbeddingSettings,
    ) => EmbeddingModelV1<string>;
}

export interface DifyProviderSettings {
    /**
     Use a different URL prefix for API calls, e.g. to use proxy servers.
     The default prefix is `https://docs.dotzero.ai/v1`.
     */
    baseURL?: string;

    /**
     API key that is being send using the `Authorization` header.
     It defaults to the `DIFY_API_KEY` environment variable.
     */
    apiKey?: string;

    /**
     Custom headers to include in the requests.
     */
    headers?: Record<string, string>;

    /**
     Custom fetch implementation. You can use it as a middleware to intercept requests,
     or to provide a custom fetch implementation for e.g. testing.
     */
    fetch?: FetchFunction;
}

/**
 Create a Mistral AI provider instance.
 */
export function createDify(
    options: DifyProviderSettings = {},
): DifyProvider {
    const baseURL =
        withoutTrailingSlash(options.baseURL) ?? 'https://docs.dotzero.ai/v1';

    const getHeaders = () => ({
        Authorization: `Bearer ${loadApiKey({
            apiKey: options.apiKey,
            environmentVariableName: 'DIFY_API_KEY',
            description: 'Dify',
        })}`,
        ...options.headers,
    });

    const createChatModel = (
        modelId: DifyChatModelId,
        settings: DifyChatSettings = {},
    ) =>
        new DifyChatLanguageModel(modelId, settings, {
            provider: 'dify.chat',
            baseURL,
            headers: getHeaders,
            fetch: options.fetch,
        });

    const createEmbeddingModel = (
        modelId: DifyEmbeddingModelId,
        settings: DifyEmbeddingSettings = {},
    ) =>
        new DifyEmbeddingModel(modelId, settings, {
            provider: 'dify.embedding',
            baseURL,
            headers: getHeaders,
            fetch: options.fetch,
        });

    const provider = function (
        modelId: DifyChatModelId,
        settings?: DifyChatSettings,
    ) {
        if (new.target) {
            throw new Error(
                'The Dify model function cannot be called with the new keyword.',
            );
        }

        return createChatModel(modelId, settings);
    };

    provider.languageModel = createChatModel;
    provider.chat = createChatModel;
    provider.embedding = createEmbeddingModel;
    provider.textEmbedding = createEmbeddingModel;
    provider.textEmbeddingModel = createEmbeddingModel;

    return provider as DifyProvider;
}

/**
 Default Dify provider instance.
 */
export const dify = createDify();
